import React, { useEffect, useState } from "react";
import axios from "axios";
import { Gavel, Edit, Delete } from '@mui/icons-material';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  Grid,
  CircularProgress,
  Snackbar,
  Avatar,
  Tooltip,
  IconButton,
  Pagination,
  TablePagination,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { fetchTeamDetails } from "../../../../Utils/AuctionFeatureUtils/AuctionUtils";
import { checkIfAdmin } from "../../../../Utils/AuthUtils";
import '../../../../assets/styles/Bidding/BiddingList.css'
import { BACKEND_DOMAIN } from "../../../../common/env";

const BidsList = () => {
  const { auctionId } = useParams();
  const [bids, setBids] = useState([]);
  const [filteredBids, setFilteredBids] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [editingBid, setEditingBid] = useState(null);
  const [newBidAmount, setNewBidAmount] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const fetchBids = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}/bids`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setBids(response.data);
        setFilteredBids(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching bids:", error);
        setLoading(false);
      }
    };

    const fetchTeams = async () => {
      try {
        const fetchedTeamDetails = await fetchTeamDetails(auctionId);
        setTeams(fetchedTeamDetails);
      } catch (error) {
        console.error("Error fetching teams:", error);
      } finally {
        setLoading(false);
      }
    };

    const verifyAdminStatus = async () => {
      try {
        const isAdminStatus = await checkIfAdmin(auctionId);
        setIsAdmin(isAdminStatus);
      } catch (error) {
        console.error("Error checking admin status:", error);
      }
    };

    fetchBids();
    fetchTeams();
    verifyAdminStatus();
  }, [auctionId]);

  // Handle search
  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    filterBids(query, selectedTeam);
  };

  // Handle team filter
  const handleTeamFilter = (e) => {
    const team = e.target.value;
    setSelectedTeam(team);
    filterBids(searchQuery, team);
  };

  // Filter bids based on search query and selected team
  const filterBids = (query, team) => {
    const filtered = bids.filter((bid) => {
      const matchesPlayer = bid.player.playerName.toLowerCase().includes(query);
      const matchesTeam = team ? bid.team.teamName === team : true;
      return matchesPlayer && matchesTeam;
    });
    setFilteredBids(filtered);
  };

  // Snackbar for no bids found
  useEffect(() => {
    if (filteredBids.length === 0 && searchQuery) {
      setOpenSnackbar(true);
    }
  }, [filteredBids, searchQuery]);

  // Edit Bid
  const handleEditBid = async (bidId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${BACKEND_DOMAIN}/auctions/${auctionId}/bids/${bidId}/edit`,
        { bidAmount: newBidAmount },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log("response from bid edit", response.data);
      const updatedBids = bids.map((bid) =>
        bid._id === bidId ? { ...bid, bidAmount: newBidAmount } : bid
      );
      setBids(updatedBids);
      setFilteredBids(updatedBids);
      setNewBidAmount("");  // Reset input field after updating
      setEditingBid(null);  // Reset editing state
    } catch (error) {
      console.error("Error editing bid:", error);
    }
  };

  // Delete Bid
  const handleDeleteBid = async (bidId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this player?");

    if(!confirmDelete) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${BACKEND_DOMAIN}/auctions/${auctionId}/bids/${bidId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updatedBids = bids.filter((bid) => bid._id !== bidId);
      setBids(updatedBids);
      setFilteredBids(updatedBids);
      window.location.reload();
    } catch (error) {
      console.error("Error deleting bid:", error);
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get current page bids
  const paginatedBids = filteredBids.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box 
      className="bids-tab-container"
      sx={{
        padding: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "#ffffff",
        minHeight: "100vh",
        margin: 0
      }}>
      {/* Search and Filter - Moved to top right and pushed above */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        width: '100%',
        maxWidth: "1200px",
        marginBottom: "10px",
        position: "relative",
        top: 0,
        gap: { xs: 1, sm: 2 },
        width: { xs: "100%", sm: "100%" },
        flexDirection: { xs: "column", sm: "row" },
        margin: "0 auto",
        padding: { xs: 1, sm: 0 }
      }}>
        <TextField
          label="Search by Player"
          variant="outlined"
          value={searchQuery}
          onChange={handleSearch}
          sx={{ 
            width: { xs: "100%", sm: "200px" },
            '& .MuiInputBase-root': {
              height: '40px' // Make the input smaller
            }
          }}
        />
        <FormControl sx={{ 
          width: { xs: "100%", sm: "200px" },
          '& .MuiInputBase-root': {
            height: '40px' // Make the input smaller
          }
        }}>
          <InputLabel>Filter by Team</InputLabel>
          <Select
            value={selectedTeam}
            onChange={handleTeamFilter}
            label="Filter by Team"
          >
            <MenuItem value="">
              <em>All Teams</em>
            </MenuItem>
            {teams.map((team) => (
                <MenuItem key={team._id} value={team.teamName}>
                {team.teamName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Loading spinner */}
      {loading ? (
        <Box sx={{ 
          display: "flex", 
          justifyContent: "center", 
          my: 2, 
          position: "relative", 
          top: 0,
          margin: "0 auto"
        }}>
          <CircularProgress size={30} />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} elevation={4} className="bids-table-container" sx={{ 
            position: "relative", 
            top: 0,
            width: "100%",
            marginTop: "10px",
            margin: "0 auto"
          }}>
            <Table className="bids-table" aria-label="bids table">
              <TableHead>
                <TableRow>
                  <TableCell className="table-header-cell">Player</TableCell>
                  <TableCell className="table-header-cell">Team</TableCell>
                  <TableCell className="table-header-cell">Bid Amount</TableCell>
                  {isAdmin && <TableCell className="table-header-cell">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedBids
                .filter((bid) => bid?.player && bid?.team)
                .map((bid) => (
                  <TableRow key={bid._id} className="table-row">
                    <TableCell>
                      <div className="player-cell">
                        <Tooltip title={bid.player.playerName}>
                          <Avatar src={`${bid.player.playerLogo}`} className="avatar" />
                        </Tooltip>
                        {bid.player.playerName}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="team-cell">
                        <Tooltip title={bid.team.teamName}>
                          <Avatar src={`${bid.team.teamLogo}`} className="avatar" />
                        </Tooltip>
                        {bid.team.teamName}
                      </div>
                    </TableCell>
                    <TableCell className="bid-amount">${bid.bidAmount}</TableCell>
                    {isAdmin && (
                      <TableCell>
                        {/* <IconButton color="primary" onClick={() => handleEditBid(bid._id)}>
                          <Edit />
                        </IconButton> */}
                        <IconButton color="error" onClick={() => handleDeleteBid(bid._id)}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Table Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredBids.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              backgroundColor: "white",
              borderRadius: "8px",
              marginTop: 2,
              "& .MuiTablePagination-toolbar": {
                fontSize: "1rem",
                fontWeight: "bold",
              },
              position: "relative",
              top: "-20px"
            }}
          />
        </>
      )}

      {/* Snackbar for no results */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message="No bids found matching your search criteria"
        sx={{ bottom: 20 }}
      />
    </Box>
  );
};

export default BidsList;